import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { first, last, map, Observable } from 'rxjs';
import { BaseService } from './_base.service';
import { IndexedDBService } from './indexed-db.service';
import moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class PersistentHttpService extends BaseService {
  db = inject(IndexedDBService);
  constructor() {
    super();
  }

  setLastSync(url, time?, id?) {
    return this.db.write('syncs', {
      url,
      last_sync_at: time,
      last_sync_id: id,
    });
  }
  getLastSync(url) {
    return new Observable<any>(observer => {
      this.db.read('syncs',url).then((data) => {
        if (data) {
          observer.next(data);
          observer.complete();
        } else {
          observer.next(null);
          observer.complete();
        }
      }).catch((error) => {
        console.error('Error reading data from IndexedDB:', error);
      });
    })
  }
  
  index(url, idbTable, params?, filterFn?, resetDB = false) {
    return new Observable<any>(observer => {
      this.db.read(idbTable).then((data) => {
        if (data && data.length > 0) {
          if (filterFn) {
            data = data.filter(filterFn);
          }
          
        }
        this.http.get(url, this.getParams(params))
          .pipe(
            first(),
            map((response: HttpResponse<any[]>) => response.body),
          ).subscribe({
            next: (response) => {
              if (response && response.length > 0) {
                if (resetDB) {
                  this.db.clear(idbTable);
                }
                response.forEach(item => {
                  if (item.id) {
                    this.db.write(idbTable, item);
                  }
                });
                
              }
              observer.next(this.mergeItems(data, response));
              observer.complete();
            },
            error: (error) => {
              console.error('Error fetching data', error);
              observer.next(data);
              observer.complete();
            }
          });
      }).catch((error) => {
        console.error('Error reading data from IndexedDB:', error);
      });
    })
  }

  create(url, idbTable, body, params?, custom_params?) {
    return new Observable<any>(observer => {
      this.http.post(url, body, this.getParams(params))
        .pipe(
          map((response: HttpResponse<any>) => response.body),
          first()
        ).subscribe({
          next: (response) => {
            if (response) {

              this.db.write(idbTable, {
                ...response,
                ...custom_params
              });
            }
            observer.next(response);
            observer.complete();
          },
          error: (error) => {
            console.error('Error posting comment', error);
            observer.error(error);
          }
        });
    })
  }

  update(url, idbTable, body, params?, custom_params?) {
    return new Observable<any>(observer => {
      this.http.put(url, body, this.getParams(params))
        .pipe(
          first(),
          map((response: HttpResponse<any>) => response.body),
        ).subscribe({
          next: (response) => {
            if (response) {
              this.db.read(idbTable, response.id).then((data) => {
                this.db.write(idbTable, {
                  ...data,
                  ...response,
                  ...custom_params,
                });
              }).catch((error) => {
                console.error('Error reading data from IndexedDB:', error);
              });
            }
            observer.next(response);
            observer.complete();
          },
          error: (error) => {
            console.error('Error updating comment', error);
            observer.error(error);
          }
        });
    })
  }

  destroy(url, id, idbTable, params?, custom_params?) {
    return new Observable<any>(observer => {
      this.http.delete(url, this.getParams(params))
        .pipe(
          first(),
          map((response: HttpResponse<any>) => response.body),
        ).subscribe({
          next: (response) => {
            if (response) {
              this.db.delete(idbTable, id);
            }
            observer.next(response);
            observer.complete();
          },
          error: (error) => {
            console.error('Error deleting comment', error);
            observer.error(error);
          }
        });
    })
  }

  softDestroy(url, id, idbTable, params?, custom_params?) {
    return new Observable<any>(observer => {
      this.http.delete(url, this.getParams(params))
        .pipe(
          first(),
          map((response: HttpResponse<any>) => response.body),
        ).subscribe({
          next: (response) => {
            if (response) {
              this.db.softDelete(idbTable, id, {
                ...response,
                ...custom_params
              });
            }
            observer.next(response);
            observer.complete();
          },
          error: (error) => {
            console.error('Error deleting comment', error);
            observer.error(error);
          }
        });
    })
  }
  mergeItems(existingItems, newItems) {
    if (!existingItems || !Array.isArray(existingItems)) {
      existingItems = [];
    }
    if (!newItems || !Array.isArray(newItems)) {
      newItems = [];
    }
    
    const mergedItems = [...existingItems];
    
    newItems.forEach(newItem => {
      const existingIndex = mergedItems.findIndex(item => item.id === newItem.id);
      if (existingIndex > -1) {
        mergedItems[existingIndex] = { ...mergedItems[existingIndex], ...newItem, listed: true };
      } else {
        newItem.listed = true;
        mergedItems.push(newItem);
      }
    });
    
    return mergedItems;
  }
}